import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { toast } from "react-toastify";
import PeopleTable, { NameCell } from "../../components/dashboardcomponents/People/PeopleTable";
import PeopleTableSkeleton from "../../components/dashboardcomponents/People/PeopleTableSkeleton";
import SearchInput from "../../components/common/SearchInputField";
import PlatformStatusChip from "../../components/platformAdmin/PlatformStatusChip";
import BusinessDetailDrawer from "../../components/platformAdmin/BusinessDetailDrawer";
import { usePlatformAdmin } from "../../components/platformAdmin/PlatformAdminContext";
import {
  fetchPlatformBusinesses,
  formatPlatformDate,
  mapBusinessListRow,
  parseBusinessesList,
} from "../../components/platformAdmin/platformAdminApi";
import { PlatformTabs } from "../../components/platformAdmin/platformUi";
import { PLATFORM_BUSINESS_TABS } from "../../constants/platformAdminDummyData";
import useDebounce from "../../hooks/useDebounce";

const cellTextSx = { color: "#405261", fontSize: 14, fontFamily: "Inter", whiteSpace: "nowrap" };
const ROWS_PER_PAGE = 20;

export default function PlatformBusinesses() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { requestAction } = usePlatformAdmin();
  const tab = searchParams.get("tab") || "all";
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 400);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drawerRow, setDrawerRow] = useState(null);

  useEffect(() => {
    setPage(0);
  }, [tab, debouncedSearch]);

  const loadList = useCallback(async () => {
    setLoading(true);
    const result = await fetchPlatformBusinesses({
      page: page + 1,
      limit: ROWS_PER_PAGE,
      search: debouncedSearch.trim(),
      status: tab,
    });
    if (!result?.success) {
      toast.error(result?.message || "Failed to load businesses");
      setRows([]);
      setTotal(0);
    } else {
      const { list, pagination } = parseBusinessesList(result);
      setRows(list.map(mapBusinessListRow).filter(Boolean));
      setTotal(pagination.total || list.length);
    }
    setLoading(false);
  }, [page, debouncedSearch, tab]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const columns = [
    {
      key: "name",
      label: "Business",
      width: "minmax(200px, 1.6fr)",
      render: (row) => <NameCell name={row.name} subtitle={row.slug || row.email || undefined} />,
    },
    {
      key: "owner",
      label: "Owner",
      width: "minmax(180px, 1.3fr)",
      render: (row) => (
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
          <Typography sx={{ ...cellTextSx, color: "#061522", overflow: "hidden", textOverflow: "ellipsis" }}>
            {row.ownerName}
          </Typography>
          <Typography sx={{ ...cellTextSx, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis" }}>
            {row.ownerEmail || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      key: "phone",
      label: "Phone",
      width: "minmax(120px, 0.8fr)",
      render: (row) => <Typography sx={cellTextSx}>{row.phone || "—"}</Typography>,
    },
    {
      key: "status",
      label: "Status",
      width: "minmax(112px, 0.7fr)",
      render: (row) => <PlatformStatusChip status={row.status} />,
    },
    {
      key: "createdAt",
      label: "Created",
      width: "minmax(110px, 0.7fr)",
      render: (row) => <Typography sx={cellTextSx}>{formatPlatformDate(row.createdAt)}</Typography>,
    },
  ];

  const actionMenu = [
    { key: "view", label: "View details" },
    { key: "activate", label: "Activate", hidden: (row) => row.status === "active" },
    { key: "suspend", label: "Suspend", danger: true, hidden: (row) => row.status !== "active" },
    { key: "ban", label: "Ban", danger: true, hidden: (row) => row.status === "banned" },
  ];

  const onAction = (type, row) => {
    if (type === "view") navigate(`/platform-admin/businesses/${row.id}`);
    else {
      requestAction(type, row, {
        onDone: () => {
          setDrawerRow(null);
          loadList();
        },
      });
    }
  };

  const tabs = PLATFORM_BUSINESS_TABS.map((t) => ({ ...t }));

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box sx={{ px: "24px", pt: "8px" }}>
        <PlatformTabs
          tabs={tabs}
          active={tab}
          onChange={(key) => setSearchParams(key === "all" ? {} : { tab: key })}
        />
      </Box>

      <Box sx={{ px: "24px", py: 3, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Box sx={{ width: 296, flex: "0 0 296px" }}>
          <SearchInput
            placeholder="Search name, slug or email..."
            value={searchInput}
            className="SearchInputField-article"
            onChange={(e) => setSearchInput(e.target.value)}
            iconPosition="left"
            iconGap={8}
            style={{ width: 296 }}
            inputStyle={{ width: 296 }}
            noTitle
            iconVariant="people"
          />
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", px: "24px" }}>
        {loading && rows.length === 0 ? (
          <PeopleTableSkeleton columns={columns} selectable={false} />
        ) : (
          <PeopleTable
            columns={columns}
            rows={rows}
            selectable={false}
            page={page}
            rowsPerPage={ROWS_PER_PAGE}
            totalCount={total}
            onChangePage={setPage}
            actionMenu={actionMenu}
            onAction={onAction}
            onRowClick={(row) => setDrawerRow(row)}
            highlightedRowId={drawerRow?.id}
            entityLabel="Businesses"
            horizontalBleed={24}
          />
        )}
      </Box>

      {drawerRow && (
        <BusinessDetailDrawer
          business={drawerRow}
          onClose={() => setDrawerRow(null)}
          onViewDetail={() => navigate(`/platform-admin/businesses/${drawerRow.id}`)}
          onActionDone={() => {
            setDrawerRow(null);
            loadList();
          }}
        />
      )}
    </Box>
  );
}
